/**
 * Landing vs chat canvas presentation.
 * One Antigravity stack — intensity split lives here, not in a second particle system.
 */

export type CanvasSurface = 'landing' | 'chat' | 'page'
export type ParticleMass = 'dust' | 'mid' | 'anchor'

export interface SurfaceTune {
  densityDivisor: number
  minCount: number
  maxCount: number
  spawnRadius: number
  springScale: number
  noiseScale: number
  alphaScale: number
  compactAlphaScale: number
  trailClearMinIdle: number
  trailClearMinActive: number
  bloomIdle: number
  bloomThinking: number
  bloomSpeaking: number
  ambientBoost: number
  structuralScale: number
  trailSpeedThreshold: number
  trailLengthScale: number
  trailAlphaScale: number
  haloScale: number
  dashAspect: number
  coreScale: number
  anchorShare: number
  midShare: number
  /** Minimum curl so the ink field never freezes after morph. */
  flowFloor: number
  handheldMinCount: number
  handheldMaxCount: number
}

export const SURFACE_TUNE: Record<CanvasSurface, SurfaceTune> = {
  landing: {
    densityDivisor: 620,
    minCount: 2400,
    maxCount: 3600,
    spawnRadius: 280,
    springScale: 0.58,
    noiseScale: 1.15,
    alphaScale: 1.18,
    compactAlphaScale: 1.02,
    trailClearMinIdle: 1,
    trailClearMinActive: 1,
    bloomIdle: 0.44,
    bloomThinking: 0.52,
    bloomSpeaking: 0.58,
    ambientBoost: 1,
    structuralScale: 0.2,
    trailSpeedThreshold: 1.05,
    trailLengthScale: 1,
    trailAlphaScale: 0.82,
    haloScale: 1,
    dashAspect: 2.2,
    coreScale: 1,
    anchorShare: 0.045,
    midShare: 0.16,
    flowFloor: 0.004,
    handheldMinCount: 420,
    handheldMaxCount: 720,
  },
  chat: {
    densityDivisor: 2400,
    minCount: 180,
    maxCount: 360,
    spawnRadius: 170,
    springScale: 0.48,
    noiseScale: 0.72,
    alphaScale: 0.9,
    compactAlphaScale: 0.72,
    trailClearMinIdle: 1,
    trailClearMinActive: 1,
    bloomIdle: 0.2,
    bloomThinking: 0.28,
    bloomSpeaking: 0.34,
    ambientBoost: 0.62,
    structuralScale: 0.12,
    trailSpeedThreshold: 2.2,
    trailLengthScale: 0.48,
    trailAlphaScale: 0.34,
    haloScale: 0.44,
    dashAspect: 1.28,
    coreScale: 1,
    anchorShare: 0.05,
    midShare: 0.16,
    flowFloor: 0.002,
    handheldMinCount: 120,
    handheldMaxCount: 240,
  },
  page: {
    densityDivisor: 1100,
    minCount: 900,
    maxCount: 1800,
    spawnRadius: 200,
    springScale: 0.52,
    noiseScale: 0.9,
    alphaScale: 0.96,
    compactAlphaScale: 0.8,
    trailClearMinIdle: 1,
    trailClearMinActive: 1,
    bloomIdle: 0.28,
    bloomThinking: 0.36,
    bloomSpeaking: 0.44,
    ambientBoost: 0.7,
    structuralScale: 0.16,
    trailSpeedThreshold: 1.45,
    trailLengthScale: 0.7,
    trailAlphaScale: 0.5,
    haloScale: 0.4,
    dashAspect: 1.6,
    coreScale: 0.9,
    anchorShare: 0.05,
    midShare: 0.16,
    flowFloor: 0.003,
    handheldMinCount: 280,
    handheldMaxCount: 480,
  },
}

export const REDUCED_MOTION_PARTICLE_CAP = 220
export const HANDHELD_BREAKPOINT_PX = 768

export function isHandheldViewport(width: number, pointerCoarse = false): boolean {
  return pointerCoarse || width < HANDHELD_BREAKPOINT_PX
}

export function getSurfaceTune(surface: CanvasSurface | undefined): SurfaceTune {
  return SURFACE_TUNE[surface ?? 'page']
}

export function resolveParticleCount(
  width: number,
  height: number,
  surface: CanvasSurface | undefined,
  reducedMotion = false,
  handheld = false,
): number {
  const tune = getSurfaceTune(surface)
  if (reducedMotion) return Math.min(tune.handheldMinCount, REDUCED_MOTION_PARTICLE_CAP)
  const compact = handheld || isHandheldViewport(width)
  const min = compact ? tune.handheldMinCount : tune.minCount
  const max = compact ? tune.handheldMaxCount : tune.maxCount
  const area = Math.max(1, width * height)
  const raw = Math.floor(area / tune.densityDivisor)
  return Math.max(min, Math.min(max, raw))
}
